import { __rest } from 'tslib';
import Api from '@teko/api';
import { accessTokenInterceptor } from '@teko/auth';
import { cleanObject } from '@teko/utils';
import { CONSUMER_ORDER_STATUSES } from './constants.es.js';
import { convertOrderDetail } from './helpers.es.js';

const cookOrder = (data, orderStatuses = CONSUMER_ORDER_STATUSES) => {
    var _a, _b, _c, _d, _e;
    return Object.assign(Object.assign({}, data), { promotions: (_a = data === null || data === void 0 ? void 0 : data.promotions) === null || _a === void 0 ? void 0 : _a.map((_a) => {
            var { gifts } = _a, rest = __rest(_a, ["gifts"]);
            return (Object.assign({ gifts: gifts || [] }, rest));
        }), computedStatus: orderStatuses.find((item) => item.status === data.status), predictCancelAt: data.predictCancelAt
            ? new Date(/((\+\d{2}:\d{2})|Z)$/.test(data.predictCancelAt)
                ? data.predictCancelAt
                : `${data.predictCancelAt}Z`)
            : undefined, paymentMethod: (_c = (_b = data === null || data === void 0 ? void 0 : data.payments) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.paymentMethod, createdAt: new Date(/((\+\d{2}:\d{2})|Z)$/.test(data.createdAt)
            ? data.createdAt
            : `${data.createdAt}Z`), shippingInfo: Object.assign({}, data.shippingInfo), shippingFee: ((_e = (_d = data === null || data === void 0 ? void 0 : data.payments) === null || _d === void 0 ? void 0 : _d[0]) === null || _e === void 0 ? void 0 : _e.totalPaid) || 0 });
};
const cookMyOrders = (data, orderStatuses = CONSUMER_ORDER_STATUSES, skuImageMapping = new Map()) => {
    return Object.assign(Object.assign({}, data), { records: data.data.map((record) => {
            var _a, _b, _c, _d, _e, _f, _g;
            return Object.assign(Object.assign({}, record), { promotions: (_a = record === null || record === void 0 ? void 0 : record.promotions) === null || _a === void 0 ? void 0 : _a.map((_a) => {
                    var { gifts } = _a, rest = __rest(_a, ["gifts"]);
                    return (Object.assign({ gifts: gifts || [] }, rest));
                }), computedStatus: orderStatuses.find((item) => item.status === record.status), predictCancelAt: record.predictCancelAt
                    ? new Date(/((\+\d{2}:\d{2})|Z)$/.test(record.predictCancelAt)
                        ? record.predictCancelAt
                        : `${record.predictCancelAt}Z`)
                    : undefined, paymentMethod: (_c = (_b = record === null || record === void 0 ? void 0 : record.payments) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.paymentMethod, createdAt: new Date(/((\+\d{2}:\d{2})|Z)$/.test(record.createdAt)
                    ? record.createdAt
                    : `${record.createdAt}Z`), shippingInfo: Object.assign(Object.assign({}, record.shippingInfo), { expectedDate: new Date((_d = record === null || record === void 0 ? void 0 : record.shippingInfo) === null || _d === void 0 ? void 0 : _d.expectedDate) }), shippingFee: ((_g = (_f = (_e = record === null || record === void 0 ? void 0 : record.serviceFee) === null || _e === void 0 ? void 0 : _e.delivery) === null || _f === void 0 ? void 0 : _f[0]) === null || _g === void 0 ? void 0 : _g.rowTotal) || 0, items: record.items.map((item) => {
                    return Object.assign(Object.assign({}, item), { imageUrl: skuImageMapping[item.sku] });
                }) });
        }) });
};
const cookOrdersWithDiscovery = async (data, terminalCode, orderStatuses, discoveryService) => {
    if (data.data.length) {
        const allItemSkus = [];
        data.data.forEach((record) => {
            record.items.forEach((item) => allItemSkus.push(item.sku));
        });
        const products = await discoveryService.getProductDetailBySkus({
            skus: allItemSkus.join(),
            terminalCode,
        });
        const skuImageMapping = products.reduce((prev, curr) => {
            var _a, _b;
            return (Object.assign(Object.assign({}, prev), { [(_a = curr.productInfo) === null || _a === void 0 ? void 0 : _a.sku]: (_b = curr.productInfo) === null || _b === void 0 ? void 0 : _b.imageUrl }));
        }, {});
        const dataWithItemImage = Object.assign(Object.assign({}, data), { records: data.data.map((record) => {
                var _a, _b;
                const sellerIdsInOrder = Array.from(new Set(record.items.map((item) => item.sellerId)));
                const productsOfSeller = products.filter((product) => product.productInfo.seller.id === sellerIdsInOrder[0]);
                const seller = (_b = (_a = productsOfSeller === null || productsOfSeller === void 0 ? void 0 : productsOfSeller[0]) === null || _a === void 0 ? void 0 : _a.productInfo) === null || _b === void 0 ? void 0 : _b.seller;
                return Object.assign(Object.assign({}, record), { seller: seller ? [seller] : [], items: record.items.map((item) => {
                        var _a;
                        const product = products.find((p) => p.productInfo.sku === item.sku);
                        return Object.assign(Object.assign({}, item), { product, imageUrl: ((_a = product === null || product === void 0 ? void 0 : product.productInfo) === null || _a === void 0 ? void 0 : _a.imageUrl) || '' });
                    }) });
            }) });
        return cookMyOrders(dataWithItemImage, orderStatuses, skuImageMapping);
    }
    return cookMyOrders(data, orderStatuses);
};
class OrderService {
    constructor(orderApiUrl) {
        this.fetchOrderDetail = async (orderId, customerId) => {
            const { data } = await this.orderApi.get(`api/v1/orders/${orderId}?customerProfileId=${customerId}`);
            return data;
        };
        this.getOrder = async ({ orderId, customerId, }) => {
            const res = await this.fetchOrderDetail(orderId, customerId);
            return res.data;
        };
        this.getOrderDetail = async ({ orderId, terminalCode, discoveryService, customerId, orderStatuses, }) => {
            var _a, _b;
            const data = await this.fetchOrderDetail(orderId, customerId);
            if ((_a = data.data.items) === null || _a === void 0 ? void 0 : _a.length) {
                const allItemSkus = [];
                data.data.items.forEach((item) => allItemSkus.push(item.sku));
                const products = await discoveryService.getProductDetailBySkus({
                    skus: allItemSkus,
                    terminalCode,
                });
                const sellers = Array.from(new Set(products.map((product) => { var _a, _b; return (_b = (_a = product === null || product === void 0 ? void 0 : product.productInfo) === null || _a === void 0 ? void 0 : _a.seller) === null || _b === void 0 ? void 0 : _b.id; })));
                const dataWithItemImage = Object.assign(Object.assign({}, data.data), { sellers: sellers.map((id) => {
                        var _a;
                        return (_a = products.find((product) => { var _a, _b; return ((_b = (_a = product === null || product === void 0 ? void 0 : product.productInfo) === null || _a === void 0 ? void 0 : _a.seller) === null || _b === void 0 ? void 0 : _b.id) === id; })) === null || _a === void 0 ? void 0 : _a.productInfo.seller;
                    }), items: data.data.items.map((item) => {
                        var _a;
                        const product = products.find((product) => product.productInfo.sku == item.sku);
                        return Object.assign(Object.assign({}, item), { imageUrl: ((_a = product === null || product === void 0 ? void 0 : product.productInfo) === null || _a === void 0 ? void 0 : _a.imageUrl) || '' });
                    }), shipments: (_b = data.data.shipments) === null || _b === void 0 ? void 0 : _b.map((shipment) => {
                        var _a;
                        const routerItems = (_a = shipment.routerItems) === null || _a === void 0 ? void 0 : _a.map((routerItem) => {
                            var _a;
                            const product = products.find((product) => product.productInfo.sku == routerItem.sku);
                            return Object.assign(Object.assign({}, routerItem), { imageUrl: ((_a = product === null || product === void 0 ? void 0 : product.productInfo) === null || _a === void 0 ? void 0 : _a.imageUrl) || '' });
                        });
                        return Object.assign(Object.assign({}, shipment), { routerItems });
                    }) });
                const cookedOrder = cookOrder(dataWithItemImage, orderStatuses);
                return convertOrderDetail(Object.assign(Object.assign({}, cookedOrder), { computedStatus: cookedOrder.computedStatus || {
                        displayStatus: cookedOrder.displayStatus,
                        color: cookedOrder.displayStatusColor,
                    } }));
            }
            return convertOrderDetail(data);
        };
        this.getOrders = async ({ statuses, terminalCode, discoveryService, offset = 0, limit = 10, orderStatuses, customerId, sort = '-createdAt', }) => {
            const getOrdersParams = new URLSearchParams(cleanObject({
                statuses,
                limit,
                customerProfileId: customerId,
                offset,
                sort,
            }));
            const getOrdersParamsStatuses = statuses ||
                CONSUMER_ORDER_STATUSES.map((orderStatus) => orderStatus.status);
            getOrdersParamsStatuses.forEach((status) => getOrdersParams.append('status', status));
            const res = await this.orderApi.get(`api/v1/orders?${getOrdersParams.toString()}`);
            if (res && res.data && !res.error) {
                return cookOrdersWithDiscovery(res.data, terminalCode, orderStatuses, discoveryService);
            }
        };
        this.importOrder = async (orderId) => {
            const url = `/api/v1/import-order?orderId=${orderId}`;
            const { data } = await this.orderApi.get(url);
            return data;
        };
        this.updateVatInfo = async (orderId, vatInfo) => {
            const url = `/api/v1/orders/${orderId}/vat-info`;
            const { data } = await this.orderApi.put(url, vatInfo);
            return data;
        };
        this.getPromotions = async (params) => {
            const { data } = await this.orderApi.get('api/v1/promotions', { params });
            return data;
        };
        this.orderApi = new Api(orderApiUrl);
        this.orderApi.addInterceptor(accessTokenInterceptor);
    }
}
class StaffOrderService extends OrderService {
    constructor() {
        super(...arguments);
        this.fetchOrderDetail = async (orderId) => {
            const { data } = await this.orderApi.get(`/api/v1/marketplace/orders/${orderId}`);
            return data;
        };
        this.updateShipmentStatus = async (shipmentStatus) => {
            const { data } = await this.orderApi.patch('/api/v1/seller/orders/status', {
                data: [shipmentStatus],
            });
            return data;
        };
        this.updateSellerOrderStatus = async (requestData) => {
            const { data } = await this.orderApi.patch('/api/v1/seller/orders/status', {
                data: requestData,
            });
            return data;
        };
        this.cancelShipment = async (shipments) => {
            await this.orderApi.post('/api/v1/shipments/cancel', {
                shipments: shipments,
            });
        };
        this.returnRequest = async (returnRequest) => {
            const { data } = await this.orderApi.post('/api/v2/return-requests', {
                data: returnRequest,
            });
            return data;
        };
        this.updateOrderAtStore = async (orderId) => {
            const { data } = await this.orderApi.post(`/api/v1/orders/${orderId}/update-at-store`, {});
            return data;
        };
        this.stockRequest = async (params) => {
            const data = await this.orderApi.post('/api/v1/stock_requests', params);
            return data;
        };
        this.updateVatInfo = async (orderId, vatInfo) => {
            const url = `/api/v1/orders/${orderId}/vat-info`;
            const { data } = await this.orderApi.put(url, vatInfo);
            return data;
        };
        this.confirmOrder = async (params) => {
            const { orderId } = params;
            const url = `/api/v1/orders/${orderId}/confirm`;
            const { data } = await this.orderApi.post(url, params);
            return data;
        };
        this.getStaffOrders = async (params) => {
            return await this.orderApi.get(`/api/v1/marketplace/orders`, {
                params,
            }, true);
        };
        this.cancelOrder = async (orderId, cancelRequest) => {
            const body = {};
            if (!!cancelRequest && !!cancelRequest.reason) {
                body.reason = cancelRequest.reason || '';
            }
            return await this.orderApi.put(`/api/v1/orders/${orderId}/cancel`, body);
        };
        this.getBill = async (params) => {
            const url = '/api/v1/bill';
            const { data } = await this.orderApi.get(url, { params });
            return data;
        };
        this.approveOrRejectOrder = async ({ orderId, action, note, }) => {
            const url = `/api/v1/orders/${orderId}/approval`;
            const body = { action, note };
            return await this.orderApi.post(url, body);
        };
    }
}

export { OrderService, StaffOrderService };
